import type { Entity } from '@backstage/catalog-model';
import { isComponentType, isKind } from '@backstage/plugin-catalog';

/**
 * The entity page layouts the portal renders. `service`, `website` and
 * `default` are component layouts; `default` also covers every kind without a
 * dedicated layout (Resource, Location, Template, ...).
 */
export type EntityPageVariant =
  | 'service'
  | 'website'
  | 'default'
  | 'api'
  | 'mcp-server'
  | 'airesource'
  | 'group'
  | 'user'
  | 'system'
  | 'domain';

/** Component-like layouts that share the standard overview. */
export const componentVariants: EntityPageVariant[] = [
  'service',
  'website',
  'default',
];

/** API layouts: `mcp-server` follows the API layout and adds its remotes. */
export const apiVariants: EntityPageVariant[] = ['api', 'mcp-server'];

/**
 * Resolves the layout for an entity with the same precedence as the former
 * legacy `EntitySwitch` tree: kind first, then component type, falling back
 * to `default`. `mcp-server` APIs and `AiResource` entities (#397) have no
 * legacy equivalent.
 */
export function entityPageVariant(entity: Entity): EntityPageVariant {
  if (isKind('component')(entity)) {
    if (isComponentType('service')(entity)) return 'service';
    if (isComponentType('website')(entity)) return 'website';
    return 'default';
  }
  if (isKind('api')(entity)) {
    return entity.spec?.type === 'mcp-server' ? 'mcp-server' : 'api';
  }
  if (isKind('airesource')(entity)) return 'airesource';
  if (isKind('group')(entity)) return 'group';
  if (isKind('user')(entity)) return 'user';
  if (isKind('system')(entity)) return 'system';
  if (isKind('domain')(entity)) return 'domain';
  return 'default';
}

/**
 * Builds an entity filter that matches the given layouts and, optionally, an
 * additional predicate (for example a plugin's own availability check).
 */
export function onVariants(
  variants: EntityPageVariant[],
  predicate?: (entity: Entity) => boolean,
): (entity: Entity) => boolean {
  return entity =>
    variants.includes(entityPageVariant(entity)) &&
    (predicate ? predicate(entity) : true);
}

export const hasGithubTeamSlug = (entity: Entity): boolean =>
  Boolean(entity.metadata.annotations?.['github.com/team-slug']);
